import React, { useEffect, useState, useContext, useRef, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ConsultaInternas, ExecutaTelaInterna } from '../lib/api';
import { GlobalContext } from '../lib/GlobalVar';
import { podeAcessarTela } from '../lib/acessoTela';
import Icon from './Icon';
import CompartilharModal from './CompartilharModal';
import Chart from './Chart';
import { PERSONALIZADOS } from '../personalizados/registry';
import ErrorBoundary from '../personalizados/ErrorBoundary';

export default function TelaInterna() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(GlobalContext);
    const [tela, setTela] = useState(null);
    const [cols, setCols] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    // Sort + filter state (precisa estar antes de qualquer early return — rules-of-hooks)
    const [sortBy, setSortBy] = useState({ col: null, dir: 'asc' });
    const [filtro, setFiltro] = useState('');
    const [compartilharAberto, setCompartilharAberto] = useState(false);
    const pageRef = useRef(null);

    useEffect(() => {
        (async () => {
            setLoading(true); setErro(null);
            const internas = await ConsultaInternas({});
            const lista = Array.isArray(internas) ? internas : (internas.telas || []);
            const t = lista.find(x => x.slug === slug);
            if (!t) { setErro('Tela não encontrada.'); setLoading(false); return; }
            setTela(t);

            // Para tipos estaticos: link/iframe/html/react, decide o render direto.
            if (t.tipo === 'link') {
                window.open(t.url, '_blank');
                navigate('/Internas');
                return;
            }
            if (t.tipo === 'iframe' || t.tipo === 'html' || t.tipo === 'react') {
                setLoading(false);
                return;
            }
            // built (Construtor): chama runtime
            const r = await ExecutaTelaInterna({ slug, args: {} });
            setLoading(false);
            if (r.Error) { setErro(r.Error); return; }
            setCols(r.colunas || []);
            setRows(r.rows || []);
        })();
    }, [slug, navigate]);

    if (loading) {
        return <div className="page"><div className="muted">Carregando…</div></div>;
    }
    if (erro) {
        return (
            <div className="page">
                <div className="page-head">
                    <div>
                        <div className="eyebrow"><span className="num">!</span> Erro</div>
                        <h1 className="page-title">Tela indisponível</h1>
                        <p className="page-sub">{erro}</p>
                    </div>
                    <button className="btn ghost sm" onClick={() => navigate('/Internas')}>← Voltar</button>
                </div>
            </div>
        );
    }
    if (!tela) return null;

    // Acesso checks (basico, no front)
    if (!podeAcessarTela(user, tela)) {
        return <Negado />;
    }

    if (tela.tipo === 'iframe') {
        return (
            <div className="page" style={{ height: 'calc(100vh - var(--topbar-h) - 40px)' }}>
                <div className="page-head"><div><div className="eyebrow"><span className="num">·</span> Iframe</div><h1 className="page-title">{tela.titulo}</h1></div></div>
                <iframe src={tela.url} title={tela.titulo} style={{ width: '100%', height: '100%', border: '1px solid var(--line)' }}/>
            </div>
        );
    }
    if (tela.tipo === 'html') {
        return (
            <div className="page">
                <div className="page-head"><div><div className="eyebrow"><span className="num">·</span> HTML</div><h1 className="page-title">{tela.titulo}</h1></div></div>
                <div className="card" dangerouslySetInnerHTML={{ __html: tela.html || '<p>Conteúdo HTML não definido.</p>' }}/>
            </div>
        );
    }
    if (tela.tipo === 'react') {
        // Renderiza o componente PERSONALIZADO diretamente (mesmo registry do PersonalizadoHost).
        // Chave = slug da interna OU último segmento da rota (ex.: /personalizados/lojas-ativas).
        const chave = (PERSONALIZADOS[tela.slug] ? tela.slug
            : (String(tela.rota || '').replace(/\/+$/, '').split('/').pop() || tela.slug));
        const entry = PERSONALIZADOS[tela.slug] || PERSONALIZADOS[chave];
        if (entry && entry.componente) {
            const Comp = entry.componente;
            return (
                <div className="page">
                    <div className="page-head"><div><div className="eyebrow"><span className="num">·</span> Página interna</div><h1 className="page-title">{tela.titulo || entry.titulo}</h1></div></div>
                    <ErrorBoundary>
                        <Suspense fallback={<div className="muted" style={{ padding: 16 }}>Carregando…</div>}>
                            <Comp />
                        </Suspense>
                    </ErrorBoundary>
                </div>
            );
        }
        // fallback: sem componente registrado -> link p/ a rota (comportamento antigo)
        return (
            <div className="page">
                <div className="page-head"><div><div className="eyebrow"><span className="num">·</span> Página interna</div><h1 className="page-title">{tela.titulo}</h1></div></div>
                <div className="card">
                    <p className="muted">Esta página referencia a rota interna <span className="mono">{tela.rota || '/'}</span>.</p>
                    <button className="btn sm" onClick={() => navigate(tela.rota || '/Dashboard')}>Ir para {tela.rota}</button>
                </div>
            </div>
        );
    }

    // built — render charts + tabela
    const colsVisiveis = (tela.colunas || []).filter(c => c.visivel !== false);
    const renderCols = colsVisiveis.length > 0 ? colsVisiveis : cols.map(c => ({ ...c, label: c.label || c.nome }));

    function toggleSort(col) {
        if (col.tipo === 'acao') return;
        if (sortBy.col === col.nome) {
            if (sortBy.dir === 'asc') setSortBy({ col: col.nome, dir: 'desc' });
            else if (sortBy.dir === 'desc') setSortBy({ col: null, dir: 'asc' });
            else setSortBy({ col: col.nome, dir: 'asc' });
        } else {
            setSortBy({ col: col.nome, dir: 'asc' });
        }
    }

    // Aplica filtro de texto livre + ordenacao
    const rowsFiltradas = (() => {
        let r = rows;
        if (filtro.trim()) {
            const q = filtro.trim().toLowerCase();
            r = r.filter(row => Object.values(row).some(v =>
                v != null && String(v).toLowerCase().includes(q)
            ));
        }
        if (sortBy.col) {
            const dir = sortBy.dir === 'asc' ? 1 : -1;
            r = [...r].sort((a, b) => {
                const va = a[sortBy.col], vb = b[sortBy.col];
                if (va == null) return 1;
                if (vb == null) return -1;
                const na = Number(va), nb = Number(vb);
                if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
                return String(va).localeCompare(String(vb), 'pt-BR') * dir;
            });
        }
        return r;
    })();

    async function executaAcao(col, row) {
        const acao = col.acao || {};
        const url = (acao.url || '').replace(/\{(\w+)\}/g, (_, k) => encodeURIComponent(row[k] != null ? row[k] : ''));
        if (!url) { window?.Swal?.fire?.({icon:'warning', title:'Ação sem URL'}); return; }
        const method = (acao.method || 'GET').toUpperCase();
        try {
            const init = { method, headers: { 'Content-Type': 'application/json' } };
            if (method !== 'GET' && method !== 'HEAD') {
                const tmpl = acao.body || '';
                const body = tmpl ? tmpl.replace(/\{(\w+)\}/g, (_, k) => JSON.stringify(row[k] != null ? row[k] : '').replace(/^"|"$/g, '')) : JSON.stringify(row);
                init.body = body;
            }
            const resp = await fetch(url, init);
            const ct = resp.headers.get('content-type') || '';
            const out = ct.includes('application/json') ? await resp.json() : await resp.text();
            const okMsg = `HTTP ${resp.status} ${resp.statusText}\n\n` + (typeof out === 'string' ? out.slice(0, 400) : JSON.stringify(out, null, 2).slice(0, 600));
            if (window?.Swal) {
                window.Swal.fire({
                    icon: resp.ok ? 'success' : 'error',
                    title: acao.label || 'Resposta',
                    html: `<pre style="text-align:left;font-size:11.5px;white-space:pre-wrap;word-break:break-word;max-height:400px;overflow:auto;">${okMsg.replace(/[<>&]/g,c=>({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]))}</pre>`,
                    width: 640,
                });
            } else {
                alert(okMsg);
            }
        } catch (e) {
            if (window?.Swal) window.Swal.fire({ icon:'error', title:'Falhou', text: e.message });
            else alert(e.message);
        }
    }

    return (
        <div className="page" ref={pageRef}>
            <div className="page-head">
                <div>
                    <div className="eyebrow">
                        <span className="num">·</span> {tela.origem === 'rest' ? 'API REST' : (tela.conexao || 'Banco')} · {rows.length} linhas
                    </div>
                    <h1 className="page-title">{tela.titulo || tela.nome}</h1>
                    <p className="page-sub">{tela.descricao || '—'}</p>
                </div>
                <div style={{ display:'flex', gap:8 }} data-no-capture>
                    <button className="btn ghost sm" onClick={() => setCompartilharAberto(true)}>
                        <Icon name="send" size={11}/> Compartilhar
                    </button>
                    <button className="btn ghost sm" onClick={() => window.location.reload()}>
                        <Icon name="history" size={11}/> Atualizar
                    </button>
                </div>
            </div>

            {(tela.charts || []).length > 0 && (
                <div className="charts-row">
                    {tela.charts.map((c, i) => <div className="widget" key={i}><Chart cfg={c} rows={rows}/></div>)}
                </div>
            )}

            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <input className="input" placeholder="Filtrar em todas as colunas..." value={filtro} onChange={e=>setFiltro(e.target.value)} style={{ maxWidth:320 }}/>
                <span className="muted" style={{ fontSize:11.5 }}>{rowsFiltradas.length} de {rows.length} {rows.length === 1 ? 'linha' : 'linhas'}</span>
                {sortBy.col && (
                    <button className="btn ghost sm" onClick={() => setSortBy({ col: null, dir: 'asc' })}>
                        ordenado por <b>{sortBy.col}</b> {sortBy.dir === 'asc' ? '↑' : '↓'} — limpar
                    </button>
                )}
            </div>
            <div className="card flat" style={{ padding: 0, overflowX: 'auto' }}>
                <table className="preview-table" style={{ width: '100%' }}>
                    <thead>
                        <tr>{renderCols.map(c => {
                            const ativo = sortBy.col === c.nome;
                            const seta = ativo ? (sortBy.dir === 'asc' ? ' ↑' : ' ↓') : '';
                            return (
                                <th key={c.nome}
                                    onClick={() => toggleSort(c)}
                                    style={{ cursor: c.tipo === 'acao' ? 'default' : 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                    title={c.tipo === 'acao' ? 'Coluna de ação' : 'Clique para ordenar'}>
                                    {(c.label || c.nome).toUpperCase()}{seta}
                                </th>
                            );
                        })}</tr>
                    </thead>
                    <tbody>
                        {rowsFiltradas.map((r, i) => (
                            <tr key={i}>
                                {renderCols.map(c => {
                                    if (c.tipo === 'acao') {
                                        return (
                                            <td key={c.nome}>
                                                <button className="btn ghost sm" onClick={() => executaAcao(c, r)}>
                                                    {c.acao?.label || c.label || 'Executar'}
                                                </button>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td key={c.nome} className={c.tipo === 'numero' || c.tipo === 'moeda' ? 'mono' : ''}>
                                            {formatCell(r[c.nome], c.tipo)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {rowsFiltradas.length === 0 && <tr><td colSpan={renderCols.length || 1} className="muted" style={{ textAlign: 'center', padding: 24 }}>{rows.length === 0 ? 'Sem registros.' : 'Nenhuma linha bate com o filtro.'}</td></tr>}
                    </tbody>
                </table>
            </div>
            <CompartilharModal
                aberto={compartilharAberto}
                onClose={() => setCompartilharAberto(false)}
                targetRef={pageRef}
                titulo={tela.titulo || tela.nome}
                textoPadrao={tela.mensagem_padrao || ''}
            />
        </div>
    );
}

function Negado() {
    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num">!</span> Acesso negado</div>
                    <h1 className="page-title">Sem permissão</h1>
                    <p className="page-sub">Esta tela é restrita.</p>
                </div>
            </div>
        </div>
    );
}

function formatCell(v, tipo) {
    if (v == null) return '—';
    if (tipo === 'moeda') return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    if (tipo === 'numero') return Number(v).toLocaleString('pt-BR');
    if (tipo === 'boolean') return v ? '✓' : '—';
    if (tipo === 'data') return new Date(v).toLocaleDateString('pt-BR');
    return String(v);
}
